//NODE MODULES
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useParams } from "react-router-dom";
import ImageViewer from "awesome-image-viewer";
import { useAtomValue } from "jotai";
import ImageGallery from "react-image-gallery";

// - Contentful
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { documentToPlainTextString } from "@contentful/rich-text-plain-text-renderer";
// - GraphQL
import { useQuery, gql } from "@apollo/client";

//KPP COMPONENTS
import { ProjectHeader, MoreProjects, Quote } from "../components/project";
import { ProjectItem } from "../components/projects";

//3rd PARTY COMPONENTS
import { HiMagnifyingGlassPlus } from "react-icons/hi2";

//STATE
import { previewContentAtom } from "../state/appState";

//HELPERS
import useScrollBlock from "../helpers/scrollBlock";

//GRAPHQL QUERY
const QUERY = gql`
	query Project($slug: String!, $isPreview: Boolean = false) {
		projectCollection(limit: 1, preview: $isPreview, where: { slug: $slug }) {
			items {
				sys {
					id
				}
				title
				summary {
					json
				}
				description1 {
					json
				}
				description2 {
					json
				}
				description3 {
					json
				}
				description4 {
					json
				}
				sectorCollection(limit: 12) {
					items {
						sys {
							id
						}
						name
						slug
					}
				}
				size
				value
				completion
				location
				client
				contractor
				heroImage {
					url(transform: { width: 1600, format: WEBP, quality: 80 })
					title
				}
				imagesCollection {
					items {
						url(transform: { width: 800, format: WEBP, quality: 80 })
						urlThumbnail: url(transform: { width: 300, format: WEBP, quality: 80 })
						title
					}
				}
				partnersCollection {
					items {
						logo {
							url(transform: { width: 800, format: WEBP, quality: 80 })
							title
						}
					}
				}
				quote {
					quote
					author
				}
				associatedProjectsCollection {
					items {
						sys {
							id
							firstPublishedAt
						}
						title
						slug
						completion
						location
						heroImage {
							url(transform: { width: 800, format: WEBP, quality: 80 })
						}
					}
				}
			}
		}
	}
`;

const Project = () => {
	const [blockScroll] = useScrollBlock();
	const params = useParams();

	//Local State
	const [project, setProject] = useState(null);
	const [pageMeta, setPageMeta] = useState({
		title: "KPP Architects",
		desc: "Default website description",
	});
	const [projectDetails, setProjectDetails] = useState(null);
	const [projectGallery, setProjectGallery] = useState([]);

	//Atom State
	const isPreviewContent = useAtomValue(previewContentAtom);

	const { data, errors, loading } = useQuery(QUERY, {
		variables: {
			slug: params.projectId,
			isPreview: isPreviewContent,
		},
	});

	useEffect(() => {
		blockScroll();
	}, []);

	useEffect(() => {
		if (data && data.projectCollection) {
			setProject(data.projectCollection.items[0]);

			const pageData = data.projectCollection.items[0];
			setPageMeta({
				title: pageData.title,
				desc: documentToPlainTextString(pageData.summary.json),
			});

			const projectDetailsArray = [];
			pageData.client && projectDetailsArray.push({ label: "Client", value: pageData.client });
			pageData.contractor && projectDetailsArray.push({ label: "Contractor", value: pageData.contractor });
			pageData.size && projectDetailsArray.push({ label: "Size", value: pageData.size });
			pageData.value && projectDetailsArray.push({ label: "Value", value: pageData.value });
			setProjectDetails(projectDetailsArray);

			const projectImages = data.projectCollection.items[0].imagesCollection.items.map((img) => {
				return { original: `${img.url}`, thumbnail: `${img.urlThumbnail}`, originalTitle: img.title, description: img.title };
			});
			setProjectGallery(projectImages);
		}
	}, [data]);

	const viewImages = (idx) => {
		const projectImages = project.imagesCollection.items.map((img) => {
			return { mainUrl: `${img.url}`, thumbnailUrl: `${img.urlThumbnail}`, description: img.title };
		});
		new ImageViewer({
			images: projectImages,
			currentSelected: idx,
			stretchImages: true,
			isZoomable: false,
		});
	};

	return (
		project && (
			<>
				<Helmet>
					<title>{pageMeta.title} :: KPP Architects</title>
					<meta name="description" content={pageMeta.desc} />
				</Helmet>
				<article className="project marginBottom-xlarge">
					<ProjectHeader
						title={project.title}
						sector={project.sectorCollection.items}
						location={project.location}
						heroImage={project.heroImage}
						details={projectDetails}
					/>
					<div className="projectContent">
						<div className="content-width marginTop-large">
							<section className="summary plusPattern marginBottom-large text-center">{documentToReactComponents(project.summary.json)}</section>
							<section className="grid-wrapper description">
								{/* Row 1 - text + img */}
								<div className="text">{documentToReactComponents(project.description1.json)}</div>
								<img src={`${project.imagesCollection.items[0].url}`} alt={project.imagesCollection.items[0].title} />

								{/* Row 2 - img */}
								<img src={`${project.imagesCollection.items[1].url}`} alt={project.imagesCollection.items[1].title} className="span-col2" />

								{/* Row 3 - img + text */}
								<img src={`${project.imagesCollection.items[2].url}`} alt={project.imagesCollection.items[2].title} />

								<div className="text">{documentToReactComponents(project.description2.json)}</div>

								{/* Row 4 - img */}
								{project.imagesCollection.items[3] && (
									<img src={`${project.imagesCollection.items[3].url}`} alt={project.imagesCollection.items[3].title} className="span-col2" />
								)}

								{/* Row 5 - text */}
								{project.description3?.json?.content[0]?.content[0]?.value?.trim() !== "" && (
									<div className="text span-col2 text-center">{documentToReactComponents(project.description3?.json)}</div>
								)}

								{/* Row 6 - img */}
								{project.imagesCollection.items[4] && (
									<img src={`${project.imagesCollection.items[4].url}`} alt={project.imagesCollection.items[4].title} className="span-col2" />
								)}

								{/* Row 7 - text */}
								{project.description4?.json?.content[0]?.content[0]?.value?.trim() !== "" && (
									<div className="text span-col2 text-center">{documentToReactComponents(project.description4?.json)}</div>
								)}

								{/* Row 8 - quote */}
								{project.quote && <Quote quote={project.quote.quote} author={project.quote.author} />}
							</section>
							<section className="marginTop-large">
								<header className="text-center paddingBottom-small">
									<h2 className="fontSize-medium">{project.title} Gallery</h2>
								</header>
								<ImageGallery items={projectGallery} />
							</section>
							{project.partnersCollection.items.length > 0 && (
								<section className="partners marginTop-large text-center">
									{/* <h3>Should we put a title here? Parterns? In Combination With?</h3> */}
									<div className="flex columns-2 gap-xlarge jContent-center aItems-center marginTop-medium">
										{project.partnersCollection.items.map((partner) => (
											<img src={partner.logo.url} alt={partner.logo.title} />
										))}
									</div>
								</section>
							)}
							{project.associatedProjectsCollection.items.length > 0 && (
								<section className="marginTop-xlarge">
									<header className="text-center paddingBottom-small">
										<h2 className="fontSize-medium">Associated Projects</h2>
									</header>
									<div className="grid-wrapper grid-columns-autoFill projects-wrapper">
										{project.associatedProjectsCollection.items.map((project) => (
											<ProjectItem
												key={`assocaitedProject-${project.sys.id}`}
												id={project.sys.id}
												title={project.title}
												slug={project.slug}
												image={project.heroImage.url}
												completed={project.completion}
												location={project.location}
											/>
										))}
									</div>
								</section>
							)}
						</div>
					</div>
				</article>
				<MoreProjects title={`More ${project.sectorCollection.items[0].name} Projects`} sector={project.sectorCollection.items[0].slug} current={project.sys.id} />
			</>
		)
	);
};

export default Project;
